import {
  applyTerminalScheduleOverrides,
  buildAvailableMenuView,
} from "@/services/menuAvailabilityService.js";

// Memoization for expensive getters (menus can be large).
// Reference-based keys keep this safe with Vue reactivity.
let _itemsCache = { lang: null, itemsRef: null, translationsRef: null, result: null };
let _availableItemRefsCache = { timeBucket: null, schedulesRef: null, ignoreSchedules: null, result: null };
let _departmentsCache = {
  timeBucket: null,
  schedulesRef: null,
  ignoreSchedules: null,
  availableLastOrderedItemsRef: null,
  translationsRef: null,
  result: null
};

function applyPriceAdjustments(items, priceAdjustType, priceAdjustAmount) {
  return items.map(item => {
    let adjustedPrice = item.originalPrice;
    if (priceAdjustType === 'percentUp') {
      adjustedPrice = item.originalPrice * (1 + priceAdjustAmount / 100);
    } else if (priceAdjustType === 'percentDecrease') {
      adjustedPrice = item.originalPrice * (1 - priceAdjustAmount / 100);
    }

    return {
      ...item,
      price: adjustedPrice,
      modifierSets: item.modifierSets.map(modifierSet => ({
        ...modifierSet,
        modifiers: modifierSet.modifiers.map(modifier => {
          let modAdjustedPrice = modifier.originalPrice;
          if (priceAdjustType === 'percentUp') {
            modAdjustedPrice = modifier.originalPrice * (1 + priceAdjustAmount / 100);
          } else if (priceAdjustType === 'percentDecrease') {
            modAdjustedPrice = modifier.originalPrice * (1 - priceAdjustAmount / 100);
          }
          return {
            ...modifier,
            price: modAdjustedPrice
          };
        })
      }))
    };
  });
}

export default {
  namespaced: true,
  state() {
    return {
      schedules: null,
      items: null,
      taxes: null,
      translations: null,
      priceAdjustType: null,
      priceAdjustAmount: null
    };
  },
  getters: {
    translationStrings: (state, getters, rootState, rootGetters) => {
      const language = rootGetters.currentLanguage;

      return state.translations?.[language] || {};
    },
    currentLanguage: (state, getters, rootState, rootGetters) => {
      return rootGetters.currentLanguage;
    },
    schedules: (state, getters, rootState) => {
      const terminalConfig = rootState.terminal?.terminalDetails?.config;
      const orderTypeId = rootState.order?.orderTypeId;
      return applyTerminalScheduleOverrides(state.schedules, terminalConfig, orderTypeId);
    },
    terminalSchedules: (state, getters, rootState) => {
      // Get terminal config and available order flows for this terminal
      const terminalConfig = rootState.terminal?.terminalDetails?.config;
      const availableOrderFlows = rootState.location?.config?.orderTypes?.filter(ot => {
        // Filter to only order flows available on this terminal
        if (terminalConfig?.orderFlows && Array.isArray(terminalConfig.orderFlows)) {
          return terminalConfig.orderFlows.includes(ot.id) && ot.active;
        }
        // If no terminal restriction, use all active order flows
        return ot.active;
      }) || [];

      // Collect all unique schedules available across these order flows
      const scheduleRefs = new Set();
      
      availableOrderFlows.forEach(orderFlow => {
        // Check for per-order-flow schedule override
        if (terminalConfig?.orderFlowSchedules && 
            Array.isArray(terminalConfig.orderFlowSchedules[orderFlow.id])) {
          // Use specific schedules for this order flow
          terminalConfig.orderFlowSchedules[orderFlow.id].forEach(ref => scheduleRefs.add(ref));
        } else if (terminalConfig?.schedules && Array.isArray(terminalConfig.schedules)) {
          // Use global terminal schedule override
          terminalConfig.schedules.forEach(ref => scheduleRefs.add(ref));
        } else {
          // Use all schedules (no restrictions)
          state.schedules?.forEach(schedule => scheduleRefs.add(schedule.ref));
        }
      });

      // Return the actual schedule objects
      return state.schedules?.filter(schedule => scheduleRefs.has(schedule.ref)) || [];
    },
    availableItemRefs: (state, getters, rootState, rootGetters) => {
      const ignoreSchedules = rootGetters['location/config'].ignoreMenuSchedules;
      const now = rootGetters.currentTime;
      const timeBucket = now instanceof Date ? Math.floor(now.getTime() / 60000) : String(now);
      const schedulesRef = getters.schedules;
      if (
        _availableItemRefsCache.result &&
        _availableItemRefsCache.timeBucket === timeBucket &&
        _availableItemRefsCache.schedulesRef === schedulesRef &&
        _availableItemRefsCache.ignoreSchedules === ignoreSchedules
      ) {
        return _availableItemRefsCache.result;
      }
      const view = buildAvailableMenuView({
        allSchedules: schedulesRef,
        terminalConfig: rootState.terminal?.terminalDetails?.config,
        orderTypeId: rootState.order?.orderTypeId,
        now,
        ignoreScheduleHours: ignoreSchedules,
      });
      const itemRefs = view.availableItemRefs;
      _availableItemRefsCache = { timeBucket, schedulesRef, ignoreSchedules, result: itemRefs };
      return itemRefs;
    },
    items: (state, getters) => {
      if (getters.currentLanguage === 'en') {
        return state.items;
      }

      const translationStrings = getters.translationStrings;
      if (
        _itemsCache.result &&
        _itemsCache.lang === getters.currentLanguage &&
        _itemsCache.itemsRef === state.items &&
        _itemsCache.translationsRef === translationStrings
      ) {
        return _itemsCache.result;
      }
      const applyTranslations = (item, translations) => {
        const translatedItem = { ...item };

        if (translations) {
          translatedItem.name = translations[item.name] || item.name;
          translatedItem.description = translations[item.description] || item.description;
        }

        translatedItem.modifierSets = item.modifierSets.map(modifierSet => ({
          ...modifierSet,
          name: translations[modifierSet.name] || modifierSet.name,
          modifiers: modifierSet.modifiers.map(modifier => ({
            ...modifier,
            name: translations[modifier.name] || modifier.name,
          })),
        }));

        return translatedItem;
      };

      if (!state.items) return [];

      const result = state.items.map(item => applyTranslations(item, translationStrings));
      _itemsCache = { lang: getters.currentLanguage, itemsRef: state.items, translationsRef: translationStrings, result };
      return result;
    },
    departments: (state, getters, rootState, rootGetters) => {
      const ignoreSchedules = rootGetters['location/config'].ignoreMenuSchedules;
      const translationStrings = getters.translationStrings;
      const now = rootGetters.currentTime; // Use reactive time from root store
      const timeBucket = now instanceof Date ? Math.floor(now.getTime() / 60000) : String(now);
      const schedulesRef = getters.schedules;
      const view = buildAvailableMenuView({
        allSchedules: schedulesRef,
        terminalConfig: rootState.terminal?.terminalDetails?.config,
        orderTypeId: rootState.order?.orderTypeId,
        now,
        ignoreScheduleHours: ignoreSchedules,
      });
      // Already merged + de-duped (and already respects ignoreScheduleHours)
      let openDepartments = view.departments || [];

      const availableLastOrderedItems = rootGetters['customer/availableLastOrderedItems'];
      if (
        _departmentsCache.result &&
        _departmentsCache.timeBucket === timeBucket &&
        _departmentsCache.schedulesRef === schedulesRef &&
        _departmentsCache.ignoreSchedules === ignoreSchedules &&
        _departmentsCache.availableLastOrderedItemsRef === availableLastOrderedItems &&
        _departmentsCache.translationsRef === translationStrings
      ) {
        return _departmentsCache.result;
      }
      if (availableLastOrderedItems.length > 0) {
        const reorderDepartment = {
          ref: 'reorder',
          name: 'Order Again',
          items: availableLastOrderedItems.map(item => ({ ref: item.itemRef, lineModifierSets: item.lineModifierSets })),
        };
        openDepartments = [reorderDepartment, ...openDepartments];
      }

      const result = openDepartments.reduce((unique, department) => {
        const existingDepartment = unique.find(dept => dept.ref === department.ref);
        if (existingDepartment) {
          existingDepartment.items = [
            ...existingDepartment.items,
            ...department.items.filter(item => 
              !existingDepartment.items.some(existingItem => existingItem.ref === item.ref)
            )
          ];
        } else {
          unique.push({
            ...department,
            name: translationStrings[department.name] || department.name,
          });
        }
        return unique;
      }, []);
      _departmentsCache = {
        timeBucket,
        schedulesRef,
        ignoreSchedules,
        availableLastOrderedItemsRef: availableLastOrderedItems,
        translationsRef: translationStrings,
        result
      };
      return result;
    },
  },
  mutations: {
    SET_MENU(state, menu) {
      state.schedules = menu.schedules;
      state.items = menu.items.map(item => ({
        ...item,
        originalPrice: item.price,
        modifierSets: item.modifierSets.map(modifierSet => ({
          ...modifierSet,
          modifiers: modifierSet.modifiers.map(modifier => ({
            ...modifier,
            originalPrice: modifier.price,
          }))
        }))
      }));
      state.items = applyPriceAdjustments(state.items, state.priceAdjustType, state.priceAdjustAmount);
      state.taxes = menu.taxes;
      state.translations = menu.translations;
    },
    SET_STOCK(state, stock) {
      const stockStatusMap = stock.reduce((map, item) => {
        map[item.itemRef] = item.status;
        return map;
      }, {});
      if (state && state.items) {
        state.items = state.items.map((item) => ({
          ...item,
          stockStatus: stockStatusMap[item.ref] || "IN_STOCK",
        }));
      }
    },
    SET_PRICE_RULE(state, { type, amount }) {
      state.priceAdjustType = type;
      state.priceAdjustAmount = amount;
      if (state.items) {
        state.items = applyPriceAdjustments(state.items, type, amount);
      }
    }
  },
  actions: {
    setPriceRule({ commit }, { type, amount }) {
      console.log('setPriceRule', type, amount);
      commit("SET_PRICE_RULE", { type, amount });
    },
  },
};
